/**
 * TanStack Router, code-based rather than file-based (decision 2026-08-19, recorded in
 * MCK-20260521-002).
 *
 * Code-based because the route table is small, and because several of these paths are
 * *contracts* rather than choices — they have to be greppable as strings:
 *
 *   - `/auth/oidc/callback` is what `KALQA_OIDC__REDIRECT_PATH`
 *     (`backend/src/kalqa/config.py`) registers with Google and Microsoft. Renaming it means
 *     re-registering the application with both providers.
 *   - `/auth/link`, `/auth/verify-email`, `/auth/confirm-link`, `/auth/reset-password` and
 *     `/invitations/accept` are the targets of emailed links, and the backend builds those
 *     URLs: each of the five is a `link_path` in the template table in
 *     `backend/src/kalqa/jobs/email_templates.py`, which `_link` renders as
 *     `{origin}{link_path}?token=…`. So **these five paths are the frontend's half of a
 *     contract whose other half is written** — renaming one here without renaming its
 *     `link_path` there mails a button that lands on nothing.
 *
 * Every token arrives as a query parameter and leaves as a `POST` body. No route in this
 * table performs a side effect on `GET`.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";

import { lazy, Suspense, type ComponentType, type ReactElement } from "react";

import { PageLoader } from "@/components";
import type { DrawingsSlice, SlotTasksSeam } from "@/features/drawings";
import {
  ActivityPage,
  activityFiltersFromSearch,
  parseActivitySearch,
} from "@/features/activity";
import {
  filtersFromSearch,
  myFiltersFromSearch,
  MyTasksPage,
  parseMyTaskSearch,
  parseTaskSearch,
  PublishedTaskCloser,
  SlotTaskList,
  TaskDetail,
  TasksPage,
  viewFromSearch,
} from "@/features/tasks";
import {
  AppChrome,
  ProjectIndexPage,
  ProjectShell,
  ProjectsPage,
} from "@/features/projects";
import {
  HomeRoute,
  isReturnPath,
  ProjectsHomeRoute,
  RequireSession,
  useActiveWorkspaceId,
} from "@/features/session";
import type { RegisterFilters } from "@/features/register";
import { parseSearchRouteSearch, SearchResultsPage } from "@/features/search";
import { useMessages } from "@/lib/i18n";
import { LONG_WAIT_MS, useLongWait } from "@/lib/useLongWait";

/**
 * What a route draws while its page's chunk is on the way.
 *
 * Nothing about the screen is known at that moment — not even its layout — which is the one
 * situation CLAUDE.md ("Loading states") reserves the page loader for. It follows the guard's
 * rule too: nothing for `LONG_WAIT_MS`, so a chunk that is in the browser's cache draws no
 * loader at all, and the building section with the sentence the shell already uses after that.
 * The chrome around it stays where it is; only the content area waits.
 */
function RoutePending() {
  const t = useMessages("app");
  const long = useLongWait(true, LONG_WAIT_MS);
  return long ? <PageLoader label={t("session.loading")} /> : null;
}

/**
 * A route's page, fetched when the route is first matched (review 2026-09-25 §3.22).
 *
 * Every page used to be a static import here, which made this file the reason the sign-in
 * page shipped the register, the transmittals, the roadmap and the studio settings — one
 * 1.5 MB chunk for a form with two fields. Each `import()` below is its own chunk, cached by
 * content hash, and a feature whose whole public surface is its page leaves the main bundle.
 *
 * **What stays static, and why.** `features/activity`, `features/tasks` and `features/search`
 * are still imported above, because the router needs their URL parsers *synchronously* in
 * `validateSearch`, and a feature is imported through its `index.ts` alone (AGENTS.md rule 9)
 * — which re-exports the page beside the parser. Splitting those three needs a second public
 * entry per feature, which is a decision for the owner rather than a change this file makes
 * on its own. A feature that an eager module already imports for its queries (`people`,
 * `drawings` via `tasks`) is likewise in the main chunk whatever this file says; the
 * `import()` is then a no-op that Vite reports at build time rather than an error.
 *
 * Typed by the page it wraps, so a route passes the same props it always did. Suspense sits
 * here rather than at the root: a boundary above the shell would blank the chrome on every
 * first visit to a tab, and the chrome is the part that is already known.
 */
function lazyPage<P extends object>(
  load: () => Promise<ComponentType<P>>,
): (props: P) => ReactElement {
  const Page = lazy(async () => ({ default: await load() }));
  return function LazyPage(props: P) {
    return (
      <Suspense fallback={<RoutePending />}>
        <Page {...props} />
      </Suspense>
    );
  };
}

// The entrance and the public pages: what a signed-out visitor sees and nothing else.
const SignInPage = lazyPage(() => import("@/features/entrance").then((m) => m.SignInPage));
const SignUpPage = lazyPage(() => import("@/features/entrance").then((m) => m.SignUpPage));
const SignInLinkPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.SignInLinkPage),
);
const ForgotPasswordPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.ForgotPasswordPage),
);
const ResetPasswordPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.ResetPasswordPage),
);
const VerifyEmailPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.VerifyEmailPage),
);
const ConfirmLinkPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.ConfirmLinkPage),
);
const OidcCallbackPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.OidcCallbackPage),
);
const InviteAcceptPage = lazyPage(() =>
  import("@/features/entrance").then((m) => m.InviteAcceptPage),
);
const LandingPage = lazyPage(() => import("@/features/landing").then((m) => m.LandingPage));
const LegalPage = lazyPage(() => import("@/features/legal").then((m) => m.LegalPage));
const SharePage = lazyPage(() => import("@/features/share").then((m) => m.SharePage));
const ShareUnavailablePage = lazyPage(() =>
  import("@/features/share").then((m) => m.ShareUnavailablePage),
);

// The project's screens.
const DrawingsPage = lazyPage(() => import("@/features/drawings").then((m) => m.DrawingsPage));
const ViewerPage = lazyPage(() => import("@/features/viewer").then((m) => m.ViewerPage));
const OverviewPage = lazyPage(() => import("@/features/overview").then((m) => m.OverviewPage));
const ReviewsPage = lazyPage(() => import("@/features/reviews").then((m) => m.ReviewsPage));
const PeoplePage = lazyPage(() => import("@/features/people").then((m) => m.PeoplePage));
const ProjectSettingsPage = lazyPage(() =>
  import("@/features/project-settings").then((m) => m.ProjectSettingsPage),
);
const ProjectSetup = lazyPage(() =>
  import("@/features/project-setup").then((m) => m.ProjectSetup),
);
const ExportsList = lazyPage(() => import("@/features/exports").then((m) => m.ExportsList));
const ExportsPanel = lazyPage(() => import("@/features/exports").then((m) => m.ExportsPanel));

// The register, the transmittals and the roadmap.
const RegisterPage = lazyPage(() => import("@/features/register").then((m) => m.RegisterPage));
const TransmittalsPage = lazyPage(() =>
  import("@/features/transmittals").then((m) => m.TransmittalsPage),
);
const TransmittalDetailRoute = lazyPage(() =>
  import("@/features/transmittals").then((m) => m.TransmittalDetailRoute),
);
const RoadmapPage = lazyPage(() => import("@/features/roadmap").then((m) => m.RoadmapPage));

// The studio, its settings, the person's own pages.
const StudioPeoplePage = lazyPage(() =>
  import("@/features/studio").then((m) => m.StudioPeoplePage),
);
const FirmDirectoryPage = lazyPage(() =>
  import("@/features/studio").then((m) => m.FirmDirectoryPage),
);
const SettingsPage = lazyPage(() => import("@/features/settings").then((m) => m.SettingsPage));
const SetupPage = lazyPage(() => import("@/features/setup").then((m) => m.SetupPage));
const ProfilePage = lazyPage(() => import("@/features/profile").then((m) => m.ProfilePage));
const InboxPage = lazyPage(() => import("@/features/inbox").then((m) => m.InboxPage));

/**
 * `features/tasks`' components in `features/drawings`' two task seams. Composed here because the
 * route is the one module allowed to know both features, and `tasks` already reads `drawings` —
 * the reverse import would be a cycle (`features/drawings/slotTasks.ts`). A module constant, so
 * the prop is one object for the life of the app rather than a fresh one per render.
 */
const SLOT_TASKS: SlotTasksSeam = { List: SlotTaskList, AfterPublish: PublishedTaskCloser };

/**
 * Search parsing, in one place. `exactOptionalPropertyTypes` is on, so an absent parameter is
 * an absent *key* rather than a present `undefined` — hence the conditional spread rather
 * than `{ token: maybeUndefined }`.
 */

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * A finite number out of the address bar, or nothing.
 *
 * Both spellings, because a search parameter arrives as a string on a cold load and as
 * whatever was navigated with on a warm one. `NaN` and `Infinity` are refused rather than
 * carried: they reach a viewport as a zoom, and a drawing that opens nowhere because somebody
 * pasted a truncated URL is worse than one that opens at fit.
 */
function optionalNumber(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(optionalString(value));
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * What `/sign-in` may be told happened somewhere else before it was reached.
 *
 * A union of literals rather than a free string, so the screen renders a sentence out of its
 * own catalog and never one out of the URL. One member today: a completed password reset,
 * which lands here because `POST /auth/password-reset` issues no session and signing in is
 * therefore the person's genuine next step (`KLQ-20260821-002` §D).
 */
export type SignInNotice = "password-reset";

/**
 * `validateSearch` for `/login` and its legacy twin, `/sign-in` — one function, so the redirect
 * cannot drop a parameter the screen reads.
 *
 * `next` survives only as an in-app path other than `/` (`features/session/next.ts`). A hostile
 * value is dropped here rather than carried to the screen, so the address bar after a refused
 * `?next=//evil.example` shows plain `/login` — and the doors run `safeNext` again anyway, since
 * a warm navigation can hand this route a search object that never went through a URL.
 */
function signInSearch(
  search: Record<string, unknown>,
): { email?: string; notice?: SignInNotice; next?: string } {
  const email = optionalString(search.email);
  const notice = optionalString(search.notice);
  const next = search.next;
  return {
    ...(email === undefined ? {} : { email }),
    ...(notice === "password-reset" ? { notice } : {}),
    ...(isReturnPath(next) ? { next } : {}),
  };
}

/**
 * The phase context, on every route inside a project (`KLQ-20260827-002`).
 *
 * **A route that forgets this drops the phase on arrival**, which reads as the filter randomly
 * resetting when somebody presses a tab and is nearly invisible in a diff — `validateSearch`
 * returns the whole search object, so an unlisted key is a deleted key. Hence one helper
 * spread into every project route rather than three lines copied nine times.
 *
 * TWIN: `PROJECT_PHASE_PARAM` in `features/projects/phase.ts`, which is what reads it.
 */
function phaseSearch(search: Record<string, unknown>): { phase?: string } {
  const phase = optionalString(search.phase);
  return phase === undefined ? {} : { phase };
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

/**
 * Sends a pre-`/w/` URL to the same screen under the workspace this session resolves to.
 *
 * `replace`, so the bookmark's own entry does not sit in the history behind the canonical
 * one — a back button that lands on a redirect and immediately redirects forward again is a
 * back button that does not work.
 */
function WorkspaceRedirect({
  screen,
}: {
  readonly screen: "people" | "settings" | "firms";
}) {
  const workspaceId = useActiveWorkspaceId();
  if (workspaceId === null) return null;
  if (screen === "people")
    return <Navigate to="/w/$workspaceId/people" params={{ workspaceId }} replace />;
  if (screen === "firms")
    return <Navigate to="/w/$workspaceId/firms" params={{ workspaceId }} replace />;
  return <Navigate to="/w/$workspaceId/settings" params={{ workspaceId }} replace />;
}

// `/` — TWO AUDIENCES, ONE ADDRESS (owner's call, 2026-09-05).
//
// Signed in, it is **the entrance and no screen** (owner, 2026-09-17). It used to be B1, the
// projects list; the owner made the task page the front door and moved B1 to `/projects`. So
// `HomeRoute` runs the setup and unclaimed forks and then redirects — to `/tasks` for everybody
// who reaches a project, to `/projects` for the person who reaches nothing. This departs from
// chat1.md §1 ("the projects list is the workspace home") on purpose; see `HomeRoute`.
//
// Signed out, it is the public landing page rather than a redirect to the entrance. That is
// the `fallback` prop rather than a route of its own on purpose: a landing page at its own
// path makes every signed-out visit to `/` a redirect, and the redirect is then what a
// first-time visitor actually arrives at. The guard's default answer — `/login` — is
// unchanged for every other protected route.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <RequireSession fallback={<LandingPage />}>
      <HomeRoute />
    </RequireSession>
  ),
});

// `/projects` — B1, the projects list (KLQ-20260819-003), at its own address since 2026-09-17.
//
// Behind the same forks as `/` — setup, and the unclaimed stub (MCK-20260812-009 §E) — because a
// person who types the address is owed the same protection as one who arrives through the root.
// Not behind the landing redirect: somebody who asked for the list gets the list. The screen is
// passed in rather than imported by `features/session`, so that module keeps no knowledge of
// `features/projects`. A signed-out visit goes to `/login?next=/projects`, like every other
// protected route.
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  component: () => (
    <RequireSession>
      <ProjectsHomeRoute>
        <ProjectsPage />
      </ProjectsHomeRoute>
    </RequireSession>
  ),
});

// The destination B1's cards point at. **An index, not a screen** (KLQ-20260819-004 §3): it
// forwards to the first tab that is both permitted and built, and renders the shell's empty
// panel when there is none. Which tabs those are is the server's answer, not a guess here —
// `GET /projects/{id}/surfaces`.
const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  validateSearch: phaseSearch,
  component: function ProjectIndexRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId" });
    return (
      <RequireSession>
        <ProjectIndexPage projectId={projectId} />
      </RequireSession>
    );
  },
});

// C1a — the drawings browser (KLQ-20260819-005). The tab the product exists for: the tree,
// the publish drawer and a slot's version history. No viewer — C1b owns that.
const projectDrawingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/drawings",
  // `?slot=` and `?comment=` are the deep-link contract (`KLQ-20260820-001` scope B). Both ids
  // travel, so opening a comment needs no lookup — a feed row already knows its drawing, and a
  // notification is rendered server-side where both are in hand. `?slot=` alone also closes
  // something C1a left open: its selection lived in component state and could not be linked to.
  // `?under=` is the slicer (`KLQ-20260820-005`), carried as one comma-separated parameter
  // rather than a repeated one: it is written by hand into chat messages, and `?under=a,b` is
  // the form a person can read. The API takes the repeated form; the translation happens in
  // `comments/api.ts`, where every other query-shape decision already lives.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    slot?: string;
    comment?: string;
    under?: string;
    object?: string;
    discipline?: string;
    type?: string;
    phase?: string;
  } => {
    const slot = optionalString(search.slot);
    const comment = optionalString(search.comment);
    const under = optionalString(search.under);
    // The rail's other three axes (`KLQ-20260820-005` §6), in `under`'s form and for its
    // reason: comma-separated because a person types these into a chat message, and the
    // repeated form the API takes is built in `features/drawings/api.ts`, where every other
    // query-shape decision already lives. `type` rather than `documentType` in the URL — the
    // parameter is read by people, and the screen's own field name is not their problem.
    const object = optionalString(search.object);
    const discipline = optionalString(search.discipline);
    const type = optionalString(search.type);
    return {
      ...(slot === undefined ? {} : { slot }),
      ...(comment === undefined ? {} : { comment }),
      ...(under === undefined ? {} : { under }),
      ...(object === undefined ? {} : { object }),
      ...(discipline === undefined ? {} : { discipline }),
      ...(type === undefined ? {} : { type }),
      ...phaseSearch(search),
    };
  },
  component: function ProjectDrawingsRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/drawings" });
    const { slot, comment, under, object, discipline, type, phase } = useSearch({
      from: "/projects/$projectId/drawings",
    });
    const navigate = useNavigate();
    const ids = (value: string | undefined): string[] =>
      value === undefined ? [] : value.split(",").filter((id) => id !== "");
    const slice: DrawingsSlice = {
      under: ids(under),
      object: ids(object),
      discipline: ids(discipline),
      documentType: ids(type),
    };
    /** The inverse, exactly — so a slice written into the URL reads back as the same slice. */
    const written = (next: DrawingsSlice) => ({
      ...(next.under.length === 0 ? {} : { under: next.under.join(",") }),
      ...(next.object.length === 0 ? {} : { object: next.object.join(",") }),
      ...(next.discipline.length === 0
        ? {}
        : { discipline: next.discipline.join(",") }),
      ...(next.documentType.length === 0
        ? {}
        : { type: next.documentType.join(",") }),
    });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="drawings">
          <DrawingsPage
            projectId={projectId}
            slotTasks={SLOT_TASKS}
            selectedId={slot ?? null}
            highlightCommentId={comment ?? null}
            slice={slice}
            onSelect={(target) => {
              // `replace`, so walking the tree does not fill the history with one entry per
              // node clicked — the back button should leave this screen, not undo a selection.
              // The slice rides along: selecting a drawing inside a slice must not silently
              // widen the view back to the project.
              void navigate({
                to: "/projects/$projectId/drawings",
                params: { projectId },
                search: {
                  // The phase is the chrome's parameter, not this screen's: it travels with
                  // every navigation inside the project, and a selection rebuilt from scratch
                  // that forgot it would reset the whole project's context on a tree click.
                  ...(phase === undefined ? {} : { phase }),
                  ...written(slice),
                  ...target,
                },
                replace: true,
              });
            }}
            onSliceChange={(next) => {
              // Not `replace`: changing what you are looking at is a navigation, and the back
              // button undoing a slice is what people expect of a view they can share.
              void navigate({
                to: "/projects/$projectId/drawings",
                params: { projectId },
                search: {
                  ...(phase === undefined ? {} : { phase }),
                  ...(slot === undefined ? {} : { slot }),
                  ...(comment === undefined ? {} : { comment }),
                  ...written(next),
                },
              });
            }}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C3 — the drawing viewer (`KLQ-20260820-001` phase 3).
//
// A **child of the drawings tab**, path-wise, so `ProjectShell` keeps `drawings` active and
// "< Back to Drawings" has a real parent rather than a guess. The router matches the more
// specific path first, so this does not shadow C1a.
//
// `?rev=` is an explicit version; absent means the slot's current one. C1a keeps its
// selection in component state and cannot be linked to — this screen deliberately does not
// inherit that, because "look at r03 of this sheet" is the single most common thing anybody
// pastes into a message about a drawing.
//
// `?comment=` is phase 1's deep link, unchanged. On this route the drawing is already in the
// path, so the `slot` half of `?slot=&comment=` has nowhere to go and does not appear.
const drawingViewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/drawings/$slotId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { rev?: string; comment?: string; phase?: string } => {
    const rev = optionalString(search.rev);
    const comment = optionalString(search.comment);
    return {
      ...(rev === undefined ? {} : { rev }),
      ...(comment === undefined ? {} : { comment }),
      ...phaseSearch(search),
    };
  },
  component: function DrawingViewerRoute() {
    const { projectId, slotId } = useParams({
      from: "/projects/$projectId/drawings/$slotId",
    });
    const { rev, comment, phase } = useSearch({
      from: "/projects/$projectId/drawings/$slotId",
    });
    const navigate = useNavigate();
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="drawings">
          <ViewerPage
            projectId={projectId}
            slotId={slotId}
            versionId={rev ?? null}
            highlightCommentId={comment ?? null}
            onOpenVersion={(next, options) => {
              // Not `replace` by default: moving between versions is a navigation, and the
              // back button returning to the one you were reading is what people expect of a
              // screen whose whole job is seeing what changed. The one exception is the screen
              // resolving `?comment=` to the pin's own sheet, which nobody navigated to —
              // pushing that would make Back a no-op on the screen just arrived at, and the
              // comment id has to survive the hop because its target has not been reached yet.
              void navigate({
                to: "/projects/$projectId/drawings/$slotId",
                params: { projectId, slotId },
                search: {
                  ...(phase === undefined ? {} : { phase }),
                  ...(next === null ? {} : { rev: next }),
                  ...(options?.keepHighlight !== true || comment === undefined
                    ? {}
                    : { comment }),
                },
                ...(options?.replace === true ? { replace: true } : {}),
              });
            }}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C10 — the project's team.
const projectOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/overview",
  validateSearch: phaseSearch,
  component: function ProjectOverviewRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/overview" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="overview">
          <OverviewPage projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C9 — the project's audit log (`MCK-20260521-009`). The filter lives in the URL for the reason
// the tasks register's does: *"what happened to Blok B last week"* is a link somebody pastes into
// a message, and the CSV export is that same link answered as a file. The three sets travel
// comma-joined — `?event=review_requested,review_decided` is the form a person can type; the
// repeated form the API wants is produced in `activity/api.ts`.
const projectActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/activity",
  validateSearch: parseActivitySearch,
  component: function ProjectActivityRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/activity" });
    const search = useSearch({ from: "/projects/$projectId/activity" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="activity">
          <ActivityPage
            projectId={projectId}
            filters={activityFiltersFromSearch(search)}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C4b — the Planliste (`MCK-20260812-011`), mounted 2026-09-10.
//
// **Every filter is in the URL**, because a register somebody narrowed is a register they send
// to somebody else — *"here is the list of what is missing for DSP"* is a link, not a
// screenshot. The multi-value axes travel comma-joined for the reason the drawings route gives:
// `?phase=a,b` is the form a person can read in a chat message, and the repeated form the API
// takes is built in `register/api.ts`.
//
// The three `no*` flags are bare presence — `?no_phase` — rather than `=true`. They are
// questions with one answer worth asking (*"which drawings has nobody filed yet?"*), and a
// parameter that has to say `=false` to mean nothing is a parameter that appears in every link.
const projectRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/register",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    under?: string;
    phase?: string;
    discipline?: string;
    type?: string;
    object?: string;
    status?: string;
    no_phase?: true;
    no_object?: true;
    no_discipline?: true;
    no_document_type?: true;
    changed_after?: string;
  } => {
    const flag = (value: unknown): true | undefined =>
      value === true || value === "" || value === "true" ? true : undefined;
    const entries = {
      under: optionalString(search.under),
      phase: optionalString(search.phase),
      discipline: optionalString(search.discipline),
      type: optionalString(search.type),
      object: optionalString(search.object),
      status: optionalString(search.status),
      no_phase: flag(search.no_phase),
      no_object: flag(search.no_object),
      no_discipline: flag(search.no_discipline),
      no_document_type: flag(search.no_document_type),
      changed_after: optionalString(search.changed_after),
    };
    // `exactOptionalPropertyTypes` is on: an absent parameter is an absent *key*, never a
    // present `undefined`.
    return Object.fromEntries(
      Object.entries(entries).filter(([, value]) => value !== undefined),
    );
  },
  component: function ProjectRegisterRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/register" });
    const search = useSearch({ from: "/projects/$projectId/register" });
    const navigate = useNavigate();

    // Local, like the drawings route's twin: three lines beside the object they build are
    // easier to read than one import, and the two routes' parameter sets are not the same.
    const ids = (value: string | undefined): string[] =>
      value === undefined ? [] : value.split(",").filter((id) => id !== "");

    const filters: RegisterFilters = {
      under: ids(search.under),
      phase: ids(search.phase),
      discipline: ids(search.discipline),
      documentType: ids(search.type),
      object: ids(search.object),
      status: ids(search.status),
      noPhase: search.no_phase === true,
      noObject: search.no_object === true,
      noDiscipline: search.no_discipline === true,
      noDocumentType: search.no_document_type === true,
      changedAfter: search.changed_after ?? null,
    };

    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="register">
          <RegisterPage
            projectId={projectId}
            filters={filters}
            onFiltersChange={(next) => {
              // The exact inverse of the object built above. Not `replace`: changing what you
              // are looking at is a navigation, and the back button undoing a filter is what
              // people expect of a view they can share.
              void navigate({
                to: "/projects/$projectId/register",
                params: { projectId },
                search: {
                  ...(next.under.length === 0
                    ? {}
                    : { under: next.under.join(",") }),
                  ...(next.phase.length === 0
                    ? {}
                    : { phase: next.phase.join(",") }),
                  ...(next.discipline.length === 0
                    ? {}
                    : { discipline: next.discipline.join(",") }),
                  ...(next.documentType.length === 0
                    ? {}
                    : { type: next.documentType.join(",") }),
                  ...(next.object.length === 0
                    ? {}
                    : { object: next.object.join(",") }),
                  ...(next.status.length === 0
                    ? {}
                    : { status: next.status.join(",") }),
                  ...(next.noObject ? { no_object: true as const } : {}),
                  ...(next.noPhase ? { no_phase: true as const } : {}),
                  ...(next.noDiscipline
                    ? { no_discipline: true as const }
                    : {}),
                  ...(next.noDocumentType
                    ? { no_document_type: true as const }
                    : {}),
                  ...(next.changedAfter === null
                    ? {}
                    : { changed_after: next.changedAfter }),
                },
              });
            }}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// The register of dispatches (`MCK-20260812-010`, close-out phase 6). The phase bar is its one
// filter, so the only search parameter it carries is the one every project route carries.
const projectTransmittalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/transmittals",
  validateSearch: phaseSearch,
  component: function ProjectTransmittalsRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/transmittals" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="transmittals">
          <TransmittalsPage projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// One transmittal, addressed by its own id — **a contract, not a choice**: it is the link a
// member recipient's e-mail carries (`transmittals.dispatch`, `/transmittals/{id}`), so renaming
// it mails a button that lands on nothing. No project in the path for the API's own reason
// (`MCK-20260812-006` A); the screen draws the project from the record once it answers.
const transmittalDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transmittals/$transmittalId",
  validateSearch: phaseSearch,
  component: function TransmittalDetailRouteComponent() {
    const { transmittalId } = useParams({ from: "/transmittals/$transmittalId" });
    return (
      <RequireSession>
        <TransmittalDetailRoute transmittalId={transmittalId} />
      </RequireSession>
    );
  },
});

// C5 — the reviews queue (`MCK-20260521-007`), sliceable since `KLQ-20260825-002`.
//
// `?under=` is the slicer, carried as one comma-separated parameter for the reason the drawings
// route gives: it gets written by hand into chat messages, and `?under=a,b` is the form a person
// can read. The API takes the repeated form; the translation happens in `reviews/api.ts`.
const projectReviewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/reviews",
  validateSearch: (search: Record<string, unknown>): { under?: string; phase?: string } => {
    const under = optionalString(search.under);
    return { ...(under === undefined ? {} : { under }), ...phaseSearch(search) };
  },
  component: function ProjectReviewsRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/reviews" });
    const { under, phase } = useSearch({
      from: "/projects/$projectId/reviews",
    });
    const navigate = useNavigate();
    const slice =
      under === undefined ? [] : under.split(",").filter((id) => id !== "");
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="reviews">
          <ReviewsPage
            projectId={projectId}
            under={slice}
            onSliceChange={(next) => {
              // Not `replace`: changing what you are looking at is a navigation, and the back
              // button undoing a slice is what people expect of a view they can share.
              void navigate({
                to: "/projects/$projectId/reviews",
                params: { projectId },
                search: {
                  ...(phase === undefined ? {} : { phase }),
                  ...(next.length === 0 ? {} : { under: next.join(",") }),
                },
              });
            }}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C7/C8 — the tasks register and the board (`KLQ-20260821-001`).
//
// **The whole filter object is in the search string**, and `features/tasks/filters.ts` owns
// both directions of that round trip. This route validates and nothing more: a filtered
// register has to be a link a person can paste into a meeting invite (§B), which is only true
// if the filter lives in the URL rather than in component state that also writes to it.
//
// `?view=hierarchy` is the default and is never written, because the bare tab URL is the plan
// and a `?view=hierarchy` on every shared link would be noise (`KLQ-20260825-002` §5).
// `?view=register` and `?view=board` are the two that say so explicitly.
const projectTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/tasks",
  validateSearch: parseTaskSearch,
  component: function ProjectTasksRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/tasks" });
    const search = useSearch({ from: "/projects/$projectId/tasks" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="tasks">
          <TasksPage
            projectId={projectId}
            filters={filtersFromSearch(search)}
            view={viewFromSearch(search)}
          />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// C8 — one task (`KLQ-20260821-001` phase 5). A **child of the tasks tab**, path-wise, for
// the viewer's reason: `ProjectShell` keeps `tasks` active and "Back to tasks" has a real
// parent. The task id is the path and nothing else — a task's number is quoted in meeting
// minutes, but the number is per-project and the id is the stable address the API answers to.
// No search parameters: the detail is one row, and the register's filter object stays the
// register's.
const projectTaskDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/tasks/$taskId",
  validateSearch: phaseSearch,
  component: function ProjectTaskDetailRoute() {
    const { projectId, taskId } = useParams({
      from: "/projects/$projectId/tasks/$taskId",
    });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="tasks">
          <TaskDetail projectId={projectId} taskId={taskId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

const projectSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/settings",
  validateSearch: phaseSearch,
  component: function ProjectSettingsRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/settings" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="settings">
          <ProjectSettingsPage projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// The project-shape wizard (`KLQ-20260825-007`) — where generation lives now that the
// *Generate* buttons are gone.
//
// **Its own route rather than a drawer**, for the reason the spec gives: deciding how a
// project divides is a thing somebody comes to do, and a drawer makes it an interruption of
// whatever screen it was opened over. A route also means the entrance after project creation
// and the entrance from project settings are the same address, and that address is
// bookmarkable and shareable — "set the project up" is a sentence one person sends another.
//
// **Not a tab, and `tab={null}` in the shell.** It is not one of chat1.md §4's seven project
// sections; the tab row stays visible so leaving is always one press away, with nothing lit.
const projectSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/setup",
  validateSearch: phaseSearch,
  component: function ProjectSetupRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/setup" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab={null}>
          <ProjectSetup projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

const projectPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/people",
  validateSearch: phaseSearch,
  component: function ProjectPeopleRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/people" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab="people">
          <PeoplePage projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// B2 — the firm's own people (KLQ-20260819-006). A workspace screen, not a project one: it
// answers *who works here*, which is the question C10's *who is on this project* is
// deliberately not the same as. `AppChrome` suppresses itself for an unclaimed home
// workspace (§E), so an invited external collaborator who reaches this URL is shown the
// screen bare rather than a firm they never created — and the endpoints behind it answer
// `409` for a caller with no workspace at all.
//
// **Under `/w/$workspaceId` since KLQ-20260819-007.** A person may work for two studios, and
// which one a workspace-scoped screen is showing belongs in the URL rather than in ambient
// state: each tab then carries its own answer, the screen is deep-linkable, and the
// wrong-company case is visible in the address bar. `/people` and `/settings` stay as
// redirects — see below — because the emails and bookmarks that already point at them were
// written before this branch existed.
const studioPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/people",
  // A named component rather than an inline arrow, because the crumb is a translated string and
  // `t` is a hook. The rail already holds these words (`app.chrome.nav.*`), so the breadcrumb
  // reads them rather than restating them — three literals here were the app's only user-facing
  // English outside the catalogues until 2026-08-21.
  component: function StudioPeopleRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("chrome.nav.people")]}>
          <StudioPeoplePage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// The studio's firm directory (`KLQ-20260820-007`, owner 2026-09-16). Workspace-scoped for
// `/people`'s reason — a person may work for two studios and each has its own address book, so
// which one is on screen belongs in the URL rather than in ambient state.
//
// **Its own screen rather than a section of settings**, which is where it spent its first day:
// reading it is membership (every architect asks *who do we work with*), writing it is admin or
// manager, and those are settings' authorities inverted. A screen everybody may read does not
// belong behind a door only managers may open.
const studioFirmsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/firms",
  component: function StudioFirmsRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("chrome.nav.firms")]}>
          <FirmDirectoryPage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// B3 — the studio's own settings (KLQ-20260819-008). Its own route rather than a tab on
// `/people`: the two have different authorities — People is readable by an architect, Settings
// is writable by a manager and readable by nobody below one, because the domain rows carry the
// verification token — and one URL should not answer to two.
const studioSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/settings",
  component: function StudioSettingsRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("chrome.nav.settings")]}>
          <SettingsPage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// The person's own screen, reached from the avatar menu.
//
// **Not under `/w/$workspaceId/`, deliberately.** Everything on it belongs to the person and
// not to a company: somebody who works for two studios has one language, one name and one
// password, and a workspace-scoped URL would imply otherwise and would make the switcher's
// "keep the screen, change the company" rule meaningless here.
//
// It is drawn inside `AppChrome`, which means a session whose home workspace is unclaimed
// gets the page without a rail (MCK-20260812-009 §E) — and therefore without the avatar that
// leads to it. That is the existing rule rather than a new hole: such a session has no chrome
// at all today. The address still works when typed.
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  // `menu.profile` and not `profile.page.title`: the crumb has to match the avatar-menu entry
  // that leads here, and both are chrome. The screen's own heading is the `profile` namespace's.
  component: function ProfileRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("menu.profile")]}>
          <ProfilePage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// The client roadmap (`KLQ-20260904-003`) — what we are building, readable by a customer.
//
// **Inside `RequireSession`, and that is the whole point of the feature.** A published roadmap
// is a free product plan for a competitor, so this page is the inverse of `/legal/$kind` two
// routes down: legal is outside the guard by statutory obligation, this is inside it by
// commercial decision. The guard here hides the *page*; what hides the *text* is that
// `GET /api/v1/roadmap` requires a session and the content never enters this bundle.
//
// Not under `/w/$workspaceId/`, for the same reason `/profile` is not: the roadmap is a
// property of the product, identical for every company somebody belongs to, and a
// workspace-scoped URL would imply a per-customer roadmap that does not exist.
const roadmapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/roadmap",
  // `menu.roadmap`, matching the avatar-menu entry that leads here — both are chrome, and a
  // crumb that disagrees with the control that opened it reads as two different pages.
  component: function RoadmapRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("menu.roadmap")]}>
          <RoadmapPage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// `/exports` — every export this firm has run (`MCK-20260812-005`).
//
// **This exact path is in an email**, so it is not free to move: `export_ready` links here with
// no token and no query string, which is the whole point of that template — a signed storage URL
// in a mailbox is a download credential for the customer's entire project that lives as long as
// the mailbox does. Renaming this route strands every completion notice already sent.
//
// Not under `/w/$workspaceId/` for the same reason the link cannot carry one: the message is
// written by a worker that knows the workspace and not the URL shape the recipient's session
// will resolve to.
const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/exports",
  component: function ExportsRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("exports.workspace.title")]}>
          <ExportsList />
        </AppChrome>
      </RequireSession>
    );
  },
});

// `/tasks` — the work a person owes, across every project and every workspace
// (`MCK-20260521-009`, owner 2026-09-12).
//
// A fourth root surface beside `/exports`, `/profile` and `/roadmap`, and outside `/w/` and
// `/projects/` for the reason the page exists: its rows come from every project of every
// workspace the caller belongs to, in one request, so no single company or project could own
// the address. Reachable for every role; the landing rule in `HomeRoute` decides who *starts*
// here, which is somebody below an architect's authority everywhere.
//
// The filter is in the URL, as the register's is (`features/tasks/filters.ts`), so the default
// "open and in progress" is the bare path and anything else is a link.
// B3 — the inbox (`MCK-20260521-009`).
//
// **At the root, not under `/w/$workspaceId/`**, and that is the spec's own decision rather
// than a convenience: an inbox is keyed on the person, not on the studio they happen to be
// acting as (`chat1.md` §3, the B3 addendum). Somebody who works for two firms owes work in
// both, and a workspace-scoped inbox would be a list they cannot trust. `/tasks` and
// `/exports` sit here for the same reason.
const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inbox",
  component: function InboxRoute() {
    const t = useMessages("app");
    return (
      <RequireSession>
        <AppChrome crumbs={[t("chrome.nav.inbox")]}>
          <InboxPage />
        </AppChrome>
      </RequireSession>
    );
  },
});

// B4 — the full search results page (`KLQ-20260905-001`, brief 06), shareable.
//
// At the root, beside `/inbox`: a search answers across every project the caller can open in
// the company the tab acts as, so it belongs to no one project's address. Typing replaces the
// entry (a keystroke is not a place to go Back to); a tab is a navigation and pushes one.
const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  validateSearch: parseSearchRouteSearch,
  component: function SearchRoute() {
    const t = useMessages("search");
    const { q, type } = useSearch({ from: "/search" });
    const navigate = useNavigate();
    return (
      <RequireSession>
        <AppChrome crumbs={[t("page.title")]}>
          <SearchResultsPage
            q={q ?? ""}
            group={type ?? null}
            onQueryChange={(next) => {
              void navigate({
                to: "/search",
                search: {
                  ...(next === "" ? {} : { q: next }),
                  ...(type === undefined ? {} : { type }),
                },
                replace: true,
              });
            }}
            onGroupChange={(group) => {
              void navigate({
                to: "/search",
                search: {
                  ...(q === undefined ? {} : { q }),
                  ...(group === null ? {} : { type: group }),
                },
              });
            }}
          />
        </AppChrome>
      </RequireSession>
    );
  },
});

const myTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  validateSearch: parseMyTaskSearch,
  // `chrome.nav.tasks`, matching the rail item that leads here — both are chrome. It was
  // `menu.tasks` while the avatar menu was the way in; the entry moved to the rail 2026-09-17.
  component: function MyTasksRoute() {
    const t = useMessages("app");
    const search = useSearch({ from: "/tasks" });
    return (
      <RequireSession>
        <AppChrome crumbs={[t("chrome.nav.tasks")]}>
          <MyTasksPage filters={myFiltersFromSearch(search)} />
        </AppChrome>
      </RequireSession>
    );
  },
});

// `/projects/$projectId/exports` — one project's copy, asked for and watched.
//
// No tab of its own yet: the tab row comes off `GET /projects/{id}/surfaces` (see
// `ProjectShell`), and adding a surface is a backend decision this phase did not make. The
// route is reachable and correct; what it is still missing is a way in that is not a typed URL.
const projectExportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/exports",
  component: function ProjectExportsRoute() {
    const { projectId } = useParams({ from: "/projects/$projectId/exports" });
    return (
      <RequireSession>
        <ProjectShell projectId={projectId} tab={null}>
          <ExportsPanel projectId={projectId} />
        </ProjectShell>
      </RequireSession>
    );
  },
});

// The pre-`/w/` paths, kept as redirects rather than deleted (KLQ-20260819-007 §3).
//
// They were the only address these two screens ever had, so they are in bookmarks and in at
// least one sentence of emailed copy. `WorkspaceRedirect` sends them to the same screen under
// the workspace the session resolves to — which for anybody with one company is the company
// they already had, and for anybody with two is the one the server would have answered with
// anyway. Deleting them would turn every one of those links into a blank page.
const legacyPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/people",
  component: () => (
    <RequireSession>
      <WorkspaceRedirect screen="people" />
    </RequireSession>
  ),
});

// `/firms` without a workspace, for the same reason `/people` has one: a link somebody types or
// pastes has to land somewhere, and the session already knows which studio.
const legacyFirmsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firms",
  component: () => (
    <RequireSession>
      <WorkspaceRedirect screen="firms" />
    </RequireSession>
  ),
});

const legacySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <RequireSession>
      <WorkspaceRedirect screen="settings" />
    </RequireSession>
  ),
});

// B0, the workspace setup wizard (KLQ-20260819-002). Signed-in only, and reachable directly:
// a session that `HomeRoute` does not force here — an invited collaborator who already has
// projects — still gets to it from the "set up your company" affordance on the home screen.
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  component: () => (
    <RequireSession>
      <SetupPage />
    </RequireSession>
  ),
});

// The published legal documents (`KLQ-20260814-007` §A). **Outside `RequireSession` by
// obligation, not by preference**: an imprint is statutory from the moment the site is
// publicly reachable, and a privacy policy behind a sign-in is one nobody subject to it can
// read. One route for every kind — they differ in text and in nothing else.
const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal/$kind",
  component: function LegalDocumentRoute() {
    const { kind } = legalRoute.useParams();
    return <LegalPage kind={kind} />;
  },
});

// `/s/$shareId` — the recipient's page (`KLQ-20260826-002`).
//
// **The path is the backend's half of a contract, like the five emailed ones above.**
// `shares/router.py:open_share` answers the token exchange with
// `Location: f"{app_origin}/s/{opened.share.id}"`, so renaming this segment strands every link
// already sent — and those links are in inboxes, not in this repository.
//
// `$shareId` is a **label, never a credential**. What renders is decided by the share cookie
// the exchange set; the id is in the address so the page can be bookmarked and so two links
// opened in one tab do not read each other's cache. A bookmark that outlives the cookie lands
// on the dead-end screen, which is the audited path rather than a way around it.
//
// **Outside `RequireSession` and outside `ProjectShell`, and both absences are asserted** by
// `scripts/guard.mjs` rule 5. The reader has no account and no company: a session guard here
// would send the one population this feature exists for to a sign-in screen, and the project
// shell assumes a workspace a share session does not name. §A — a document, not an application.
//
// **The deep state is the whole route table below this page.** §A: one screen, no sub-pages,
// and the reader's place in the drawing lives in the search parameters so a reload lands where
// they were. `page` is the sheet's page; `zoom`, `cx` and `cy` are the viewport, in the image
// coordinates OpenSeadragon uses. Nothing else goes here — and deliberately no `sheet`: a
// version share is one sheet, or two rendered side by side when a comparison base was sent,
// so there is nothing for such a parameter to select.
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$shareId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { page?: number; zoom?: number; cx?: number; cy?: number } => {
    const page = optionalNumber(search.page);
    const zoom = optionalNumber(search.zoom);
    const cx = optionalNumber(search.cx);
    const cy = optionalNumber(search.cy);
    return {
      ...(page === undefined ? {} : { page: Math.max(Math.trunc(page), 0) }),
      ...(zoom === undefined || zoom <= 0 ? {} : { zoom }),
      ...(cx === undefined ? {} : { cx }),
      ...(cy === undefined ? {} : { cy }),
    };
  },
  component: function ShareRoute() {
    const { shareId } = useParams({ from: "/s/$shareId" });
    const { page, zoom, cx, cy } = useSearch({ from: "/s/$shareId" });
    const navigate = useNavigate();
    return (
      <SharePage
        shareId={shareId}
        view={{
          page: page ?? 0,
          zoom: zoom ?? null,
          x: cx ?? null,
          y: cy ?? null,
        }}
        onView={(next) => {
          // `replace`, because a pan is not a place somebody navigated to: without it a minute
          // of reading fills the history stack and the back button stops being an exit.
          void navigate({
            to: "/s/$shareId",
            params: { shareId },
            search: {
              ...(next.page === 0 ? {} : { page: next.page }),
              ...(next.zoom === null ? {} : { zoom: next.zoom }),
              ...(next.x === null ? {} : { cx: next.x }),
              ...(next.y === null ? {} : { cy: next.y }),
            },
            replace: true,
          });
        }}
      />
    );
  },
});

// `/s/unavailable` — where every dead share link lands (issue 169, 2026-09-24).
//
// **Also the backend's half of a contract.** `shares/router.py:open_share` answers an expired,
// a revoked and a never-existing token alike with `303` to this exact path
// (`UNAVAILABLE_PATH` there — retro rule 12: rename one, rename both). It renders the one
// dead-end screen and asks the API nothing; the address carries no reason and no id.
//
// A static segment, so the router ranks it above `/s/$shareId` — `unavailable` is never read as
// a share id. Outside `RequireSession` and `ProjectShell` for the reason the share route is.
const shareUnavailableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/unavailable",
  component: ShareUnavailablePage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  // CANONICAL SINCE 2026-09-05, when `/` became the landing page: the two doors are `/login`
  // and `/signup` — what a visitor types, and what the landing page links to. `/sign-in` and
  // `/sign-up` still resolve (see the two legacy routes below), because links to them are
  // already in inboxes and in people's history.
  path: "/login",
  // `email` is a prefill and nothing more — it is never a credential and never trusted.
  // Invite acceptance passes the address the invitation was sent to, so the invitee does not
  // retype it one screen after proving they hold that mailbox.
  //
  // `notice` is an act completed on another screen, carried here because this is where its
  // result is useful: somebody who has just chosen a new password arrives at
  // `/auth/reset-password`'s only possible next step, since that endpoint issues no session.
  // It is a closed set of one value and anything else is dropped, so a hand-typed parameter
  // cannot put an arbitrary string in front of a reader.
  //
  // `next` is where the sign-in returns to (`MCK-20260521-009`): the address `RequireSession`
  // was guarding when it sent the caller here. Anyone can mail a link carrying one, so it is
  // kept only when it is an in-app path (`signInSearch`), and every door runs it through
  // `safeNext` again before following it.
  validateSearch: signInSearch,
  component: SignInPage,
});

// The open front door. No search parameters at all, on purpose: an address arriving in the
// URL of a *signup* form is a value a third party chose for the person filling it in, and
// there is no invitation on this path to make that legitimate the way there is on `/sign-in`.
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignUpPage,
});

// The two old paths, kept as redirects rather than deleted. Neither is a written promise the
// way the five `/auth/*` link targets are — no email template and no identity provider names
// either of them — so this costs one component each and buys back every bookmark and pasted
// link that predates the rename.
//
// `replace`, so the old address does not sit in the history behind the new one: a back button
// that lands on a redirect and is immediately sent forward again is a back button that does
// not work.
const legacySignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  // The search travels across verbatim — `?email=` from invite acceptance, the `?notice=`
  // a completed password reset arrives with, and the `?next=` a guarded deep link carries. A redirect that drops them lands the person on a
  // blank form, having thrown away the one thing the previous screen said.
  validateSearch: signInSearch,
  component: function LegacySignInRoute() {
    const search = useSearch({ from: "/sign-in" });
    return <Navigate to="/login" search={search} replace />;
  },
});

const legacySignUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: () => <Navigate to="/signup" replace />,
});

const oidcCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  // CONTRACT: `KALQA_OIDC__REDIRECT_PATH`. Do not rename without re-registering the redirect
  // URI with every configured identity provider.
  path: "/auth/oidc/callback",
  validateSearch: (
    search: Record<string, unknown>,
  ): { code?: string; state?: string } => {
    const code = optionalString(search.code);
    const state = optionalString(search.state);
    return {
      ...(code === undefined ? {} : { code }),
      ...(state === undefined ? {} : { state }),
    };
  },
  component: OidcCallbackPage,
});

const signInLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/link",
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = optionalString(search.token);
    return token === undefined ? {} : { token };
  },
  component: SignInLinkPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/verify-email",
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = optionalString(search.token);
    return token === undefined ? {} : { token };
  },
  component: VerifyEmailPage,
});

const confirmLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/confirm-link",
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = optionalString(search.token);
    return token === undefined ? {} : { token };
  },
  component: ConfirmLinkPage,
});

// The recovery pair (`KLQ-20260821-002` §D). Anonymous by necessity: the person who needs
// them is by definition the person who cannot get in.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  // The `link_path` of the `password_reset` template
  // (`backend/src/kalqa/jobs/email_templates.py`) names this path. Renaming it strands every
  // link already sitting in somebody's inbox.
  path: "/auth/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = optionalString(search.token);
    return token === undefined ? {} : { token };
  },
  component: ResetPasswordPage,
});

const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/accept",
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = optionalString(search.token);
    return token === undefined ? {} : { token };
  },
  component: InviteAcceptPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  projectsRoute,
  projectRoute,
  projectDrawingsRoute,
  drawingViewerRoute,
  projectPeopleRoute,
  projectSettingsRoute,
  projectSetupRoute,
  projectRegisterRoute,
  projectTransmittalsRoute,
  transmittalDetailRoute,
  projectReviewsRoute,
  projectTasksRoute,
  projectTaskDetailRoute,
  projectActivityRoute,
  projectOverviewRoute,
  studioPeopleRoute,
  studioFirmsRoute,
  studioSettingsRoute,
  profileRoute,
  roadmapRoute,
  exportsRoute,
  myTasksRoute,
  inboxRoute,
  searchRoute,
  projectExportsRoute,
  legacyPeopleRoute,
  legacyFirmsRoute,
  legacySettingsRoute,
  setupRoute,
  legalRoute,
  shareUnavailableRoute,
  shareRoute,
  signInRoute,
  signUpRoute,
  legacySignInRoute,
  legacySignUpRoute,
  oidcCallbackRoute,
  signInLinkRoute,
  verifyEmailRoute,
  confirmLinkRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteAcceptRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
